package horcrux.frontend.common.views.documents

import horcrux.common.api.dtos.types.DocumentChunkID
import horcrux.common.api.dtos.types.DocumentChunkIndex
import horcrux.common.api.dtos.types.DocumentMetadata
import horcrux.common.api.dtos.types.DocumentType
import horcrux.common.api.dtos.types.ImageDocumentChunk
import horcrux.common.api.dtos.types.SimpleDocumentChunk
import horcrux.common.api.dtos.types.TextDocumentChunk
import horcrux.common.utils.ifThen
import horcrux.frontend.common.GlobalAppState
import horcrux.frontend.common.MapState
import horcrux.frontend.common.State
import horcrux.frontend.common.analytics.ScreenViewEvent
import horcrux.frontend.common.clients.DocumentChunkClient
import io.ktor.util.encodeBase64
import io.ktor.util.logging.KtorSimpleLogger
import js.objects.jso
import kotlinx.browser.window
import mui.material.CircularProgress
import mui.material.Link
import mui.material.Pagination
import mui.material.Typography
import mui.material.styles.TypographyVariant
import mui.system.sx
import react.FC
import react.Props
import react.dom.html.ReactHTML.img
import react.dom.html.ReactHTML.p
import react.useEffect
import web.cssom.Height
import web.cssom.ObjectFit
import web.cssom.WhiteSpace
import web.cssom.pct

external interface DocumentBodyProps : Props {
    var documentMetadata: DocumentMetadata
    var globalAppState: GlobalAppState
}

val DocumentBody = FC<DocumentBodyProps> { props ->
    val currentDocumentChunkIndexState = State(DocumentChunkIndex(0))
    val documentChunkByIndexState = MapState<DocumentChunkIndex, SimpleDocumentChunk>()
    val currentDocumentChunkIndex = currentDocumentChunkIndexState.get()
    val logger = KtorSimpleLogger("DocumentBody")
    useEffect(currentDocumentChunkIndex) {
        val documentChunkByIndex = documentChunkByIndexState.get()
        props.globalAppState.ifThen(currentDocumentChunkIndex !in documentChunkByIndex) {
            this.submitBackendAPIRequestAsync { backendClient ->
                val documentChunkID = DocumentChunkID(
                    documentID = props.documentMetadata.documentID,
                    documentChunkIndex = currentDocumentChunkIndex,
                )

                val getDocumentResponse =
                    backendClient.getDocumentChunk(documentChunkID, props.documentMetadata.collectionID).bind()
                val documentChunk = DocumentChunkClient.submit(getDocumentResponse.documentChunkURL).bind()
                documentChunkByIndexState.update { documentChunkByIndex ->
                    when (documentChunk) {
                        is SimpleDocumentChunk -> documentChunkByIndex + (currentDocumentChunkIndex to documentChunk)
                        else -> documentChunkByIndex
                    }
                }
            }
            this
        }
    }

    Typography {
        this.sx {
            this.whiteSpace = WhiteSpace.preLine
        }
        this.variant = TypographyVariant.caption
        +"Document URL: "
        Link {
            val url = props.documentMetadata.getSignedDocumentURL.value
            +(url.split("?").firstOrNull() ?: url)
            this.variant = "p"
            this.href = url
            this.onClick = {
                it.preventDefault()
                props.globalAppState.analyticsClient.log(ScreenViewEvent.DOCUMENT_SOURCE)
                window.open(url, "_blank", "noreferrer")
            }
        }
        +"\nDocument Type: ${props.documentMetadata.documentType}"
        +"\n\nWe have broken up your document into chunks so we can more easily search them to answer your questions.\nYou can explore them here: "
    }
    Pagination {
        this.count = props.documentMetadata.numChunks.value
        this.onChange = { _, value ->
            val documentChunkIndex = DocumentChunkIndex(value.toInt() - 1)
            currentDocumentChunkIndexState.set(documentChunkIndex)
        }
    }

    val srcSupplierMaybe = { documentMetadata: DocumentMetadata, documentChunk: ImageDocumentChunk ->
        when (documentMetadata.documentType) {
            DocumentType.IMAGE_JPEG -> "data:image/jpeg;base64"
            DocumentType.IMAGE_PNG -> "data:image/png;base64"
            else -> {
                logger.warn("Unexpected document type: ${documentMetadata.documentType}")
                null
            }
        }?.let { "$it,${documentChunk.chunkByteArray.encodeBase64()}" }
    }

    when (val documentChunk = documentChunkByIndexState.get()[currentDocumentChunkIndex]) {
        is ImageDocumentChunk -> srcSupplierMaybe(props.documentMetadata, documentChunk)?.let { src ->
            img {
                this.src = src
                this.style = jso {
                    this.width = 100.pct
                    this.height = "auto".unsafeCast<Height>()
                    this.objectFit = ObjectFit.contain
                }
            }
        }

        is TextDocumentChunk -> p {
            +documentChunk.betterToString()
        }

        else -> CircularProgress {}
    }
}
